<script>
import {useLandingPageStore} from "../../../stores/landingPage";
import AccordionItem from "../../components/accordion/AccordionItem.vue";
import ImagePicker from "./imagePicker.vue";
import ToggleSwitch from "../../components/inputs/ToggleSwitch.vue";
import TabNavigation from "../../components/TabNavigation.vue";
import ColorListPicker from "../../components/inputs/ColorListPicker.vue";
import BaseModal from "../../components/BaseModal.vue";
import Icon from "../../components/Icon.vue";
import DragNDrop from "../brand/dragNDrop.vue";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import CampaignSorter from "../../components/landing/CampaignSorter.vue";
import Popup from "../../components/popup.vue";
import CircleColorPicker from "@/javascript/views/clientDashboard/brand/CircleColorPicker.vue";
import {QuillEditor} from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import Subtitle from "@/javascript/views/components/Typography/Subtitle.vue";
import ImageCropper from "@/javascript/views/components/ImageCropper.vue";
import {useOrganizationStore} from "@/javascript/stores/organizations";
import LoadingSpinner from "../../components/loadingSpinner.vue";
import LayoutManager from "./LayoutManager.vue";
import ImagePickerCard from "../../components/ImagePickerCard.vue";
import {QrcodeCanvas} from "qrcode.vue";
import {useRoute, useRouter} from "vue-router";
import QRComponent from "../../components/QRComponent.vue";
import {computed} from "vue";
import {useUserStore} from "../../../stores/user";

export default {
  name: "LandingBuilderControlBar",
  components: {
    QRComponent,
    QrcodeCanvas,
    LayoutManager,
    ImagePickerCard,
    LoadingSpinner,
    ImageCropper,
    Subtitle,
    CircleColorPicker,
    Popup,
    CampaignSorter,
    DragNDrop,
    Icon,
    BaseModal,
    ColorListPicker, TabNavigation, ToggleSwitch, ImagePicker, AccordionItem, QuillEditor
  },
  props: {
    previewMode: {
      type: String,
      default: 'desktop'
    }
  },
  emits: ['update:previewMode', 'save', 'cancel', 'accordion-click', 'publish'],
  setup() {
    const router = useRouter();
    const route = useRoute();
    const landingStore = useLandingPageStore();
    const userStore = useUserStore();
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();
    const landingPage = computed(() => landingStore.draftLayout?.pages?.find((page) => page.page_type === 'landing_page') || {});
    const components = computed(() => landingPage.value?.components?.filter(c => c.archived !== true) || []);
    const allComponents = computed(() => landingPage.value?.components || []);
    return {
      route,
      router,
      landingStore,
      organizationStore,
      analyticEventStore,
      richText: '',
      landingPage,
      components,
      allComponents,
      userStore,
    };
  },
  data() {
    return {
      checked: false,
      showImageColorPicker: false,
      showImageUploader: false,
      croppingOriginalFile: null,
      croppingImageSrc: null,
      isUploadingCroppedImage: false,
      croppingTargetItem: null,
      imageColorPickerItem: null,
      activeTab: "edit",
      showCampaignSortModal: false,
      popupMessage: "",
      accordionRefs: [],
      justPublished: true,
      uploadMode: null,
      pageData: [],
      rearranging: false,
      removeImageConfirmation: false,
      removeType: null,
      removeComponentConfirmation: false,
      deleteConfirmation: false
    };
  },
  computed: {
    selectedResourceId: {
      get() {
        return this.imageColorPickerItem?.resourceId ?? this.imageColorPickerItem?.id;
      },
      set(value) {
        //Separating Branding image picker from the rest.
        if (this.imageColorPickerItem) {
          const isGlobalSetting = this.imageColorPickerItem === this.landingStore.localBrand.logo
            || this.imageColorPickerItem === this.landingStore.localBrand.favicon;
          if (!isGlobalSetting) {
            this.landingStore.dirtyDraft = true;
          }
          if ('resourceId' in this.imageColorPickerItem) {
            this.imageColorPickerItem.resourceId = value;
          } else {
            this.imageColorPickerItem.id = value;
          }
        }
      }
    },
    iconTooltipPosition() {
      return window.innerWidth <= 1366 ? {
        top: '100%',
        bottom: 'auto',
        left: '100%',
        transform: 'translateX(-60%)',
        minWidth: '150px',
        maxWidth: '200px',
      } : {
        top: '100%',
        bottom: 'auto',
        left: '50%',
        transform: 'translateX(-50%)'
      };
    },
    colorOptions() {
      let options = [];
      const brand = this.landingStore.localBrand;
      this.brandColors = brand;

      options.push(brand.color_primary_light);
      options.push(brand.color_primary);
      options.push(brand.color_primary_dark);
      options.push(brand.color_accent_light);
      options.push(brand.color_accent);
      options.push(brand.color_accent_dark);


      options.push(`linear-gradient(180deg, ${brand.color_primary_light} 0%, ${brand.color_primary_dark} 100%)`); // Some basic CSS Gradients
      options.push(`linear-gradient(0deg, ${brand.color_primary_light} 0%, ${brand.color_primary_dark} 100%)`);
      options.push(`linear-gradient(180deg, ${brand.color_accent} 50%, ${brand.color_accent_dark} 100%)`);
      options.push(`linear-gradient(0deg, ${brand.color_accent} 50%, ${brand.color_accent_light} 100%)`);
      return options;
    },
    canPublish() {
      return !this.landingStore.dirtyDraft &&
        !this.landingStore.publishing;
    },
    canSaveOrDiscard() {
      return this.landingStore.dirtyDraft;
    },

    brandingChanged() {
      const logoChange = this.landingStore.localBrand.logo?.id !== this.landingStore.brand.logo?.id;
      const iconChange = this.landingStore.localBrand.favicon?.id !== this.landingStore.brand.favicon?.id;
      return (this.landingStore.brand?.color_primary !== this.landingStore.localBrand.color_primary)
        || (this.landingStore.brand?.color_accent !== this.landingStore.localBrand?.color_accent) || logoChange || iconChange;
    },

    saveGlobalsEnabled() {
      const logoChanged = this.landingStore.localBrand.logo?.id !== this.landingStore.brand.logo?.id;
      const iconChanged = this.landingStore.localBrand.favicon?.id !== this.landingStore.brand.favicon?.id;
      return (this.brandingChanged || logoChanged || iconChanged) && this.landingStore.localBrand?.color_primary;
    },

    // Returns the custom sort order for campaigns/ funds.
    // Note: this is somewhat hard coded right now and only supports one list.
    customSortOrder() {
      const component = this.pageData?.find((component) => component.component_type === 'donatable_grid');
      return component?.component_data?.order?.value || [];
    },


  },
  watch: {
    components: {
      handler() {
        this.setData();
      },
      deep: true, // track changes inside the array
      immediate: true // run once on mount
    }
  },

  mounted() {
    this.setData();


  },

  methods: {
    setData() {
      const data = [
        {
          order: -1,
          component_type: 'site_settings',
          sectionTitle: "Global Site Settings",
          siteSettings: true,
          componentToDelete: null,
        },
        ...this.components
      ];

      this.pageData = data.sort((a, b) => a.order - b.order);
    },
    switchTab(tab) {
      this.analyticEventStore.logEvent({
        action: `builder-switch-to-${tab}-tab`,
        metadata: {
          from_tab: this.activeTab,
          to_tab: tab
        }
      });
      this.activeTab = tab;
    },
    openImageColorPickerModal(item) {
      this.imageColorPickerItem = item;
      this.croppingTargetItem = item;
      this.croppingImageSrc = null;
      this.showImageColorPicker = true;
    },
    clearImage() {
      // Clears the selected background image when a color is selected
      if (this.imageColorPickerItem) {
        this.imageColorPickerItem.resourceId = null;
      }
    },

    accordionClick(index, component) {
      this.$emit('accordion-click', component);
      this.collapseOthers(index);
    },

    collapseOthers(index) {
      this.accordionRefs.forEach((accordion, i) => {
        if (i === index) {
          if (accordion.show) {
            accordion.collapse();
            this.$emit('accordion-click', null);
          } else {
            accordion.expand();
          }
        } else {
          accordion.collapse();
        }
      });
    },
    removeImage(item) {
      if (item) {
        item.resourceId = null;
        this.landingStore.dirtyDraft = true;
      }
    },
    //Remove icon/logo methods
    confirmRemoveLogo() {
      this.removeType = 'logo';
      this.removeImageConfirmation = true;
    },
    confirmRemoveIcon() {
      this.removeType = 'icon';
      this.removeImageConfirmation = true;
    },
    removeLogo() {
      this.landingStore.localBrand.logo.id = null;
      this.removeImageConfirmation = false;
    },
    removeIcon() {
      this.landingStore.localBrand.favicon.id = null;
      this.removeImageConfirmation = false;
    },
    handleRemoveConfirm() {
      if (this.removeType === 'logo') {
        this.removeLogo();
      } else {
        this.removeIcon();
      }
    },
    truncateFileName(fileName) {
      if (!fileName || fileName.length <= 10) return fileName;
      return fileName.slice(0, 5) + '.....' + fileName.slice(-9);
    },
    saveCampaignSorting(campaigns) {
      const component = this.pageData?.find((component) => component.component_type === 'donatable_grid');
      this.landingStore.setDonatableCustomSortOrder(campaigns, component);
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'builder-sort-campaigns' });
      this.showPopup("Campaign Order Updated!");
    },
    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();
    },
    handleSave() {
      this.$emit("save");
    },

    handleDiscard() {
      this.$emit("cancel");
    },
    handlePublish() {
      this.$emit("publish");
    },
    onFieldChange(item, component) {
      if (item.validation) {
        // call the validation function if present
        if (item.validation(item.value)) {
          item.error = null;
        } else {
          item.error = item.errorMessage || "Not valid";
        }
      }
      this.updateVideoComponent(item, component);

      this.landingStore.dirtyDraft = true;
    },
    updateVideoComponent(item, component) {
      // Auto-check muted when autoplay is enabled for video components
      if (component?.component_type === 'video' && item.label === 'Autoplay Video' && item.value) {
        component.component_data.muted.value = true;
      }

      // Prevent unchecking muted when autoplay is on for video components
      if (component?.component_type === 'video' && item.label === 'Muted' && !item.value && component.component_data?.autoplay?.value) {
        item.value = true;
        return;
      }
    },
    onImageSelected(file) {
      this.croppingOriginalFile = file;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.croppingImageSrc = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    // close the section‐picker and reset its target
    closeImageColorPickerModal() {
      this.showImageColorPicker = false;
      this.imageColorPickerItem = null;
      this.croppingTargetItem = null;
    },

    // split upload: section vs. library
    async onImageCropComplete(croppedDataUrl) {
      const name = this.croppingOriginalFile?.name || "cropped-image.png";
      this.isUploadingCroppedImage = true;
      this.resetImageCropper();
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'builder-upload-images' });

      try {
        if (this.imageColorPickerItem) {
          const targetItem = this.imageColorPickerItem;
          const resourceId = await this.landingStore.uploadCroppedImage(croppedDataUrl, name);
          if ('resourceId' in targetItem) {
            targetItem.resourceId = resourceId;
          } else {
            targetItem.id = resourceId;
          }
          this.showPopup("Image added!");
        } else {
          await this.landingStore.uploadCroppedImage(croppedDataUrl, name);
          this.showPopup("Image added to library!");
        }
      } catch (err) {
        this.showPopup("Something went wrong!");
        console.error('[LandingBuilderControlBar] upload failed:', err);
      } finally {
        this.isUploadingCroppedImage = false;
        this.showImageUploader = false;
      }
    },

    resetImageCropper() {
      this.croppingImageSrc = null;
      this.croppingOriginalFile = null;
    },

    openUploadFromPicker() {
      // ensure the crop/upload flow knows which item to assign to
      this.croppingTargetItem = this.imageColorPickerItem;
      this.showImageColorPicker = false;
      this.showImageUploader = true;
    },

    componentHeaderTitle(type, component) {
      const typeConfig = {
        site_settings: { label: 'Branding' },
        header: { label: 'Header' },
        hero_banner: { label: 'Page Banner' },
        donatable_grid: { label: 'Giving Section' },
        footer: { label: 'Footer' },
        article: { label: 'Content Section', usesTitle: true },
        video: { label: 'Video', usesTitle: true }
      };

      const config = typeConfig[type];
      if (!config) return type;
      if (!config.usesTitle) return config.label;

      const title = component.component_data?.title?.value ||
                    (typeof component.component_data?.title === 'string' ? component.component_data.title : '');

      if (title?.trim()) return title;

      if (component.sequence_number != null) return `${config.label} #${component.sequence_number}`;

      return `${config.label}`;
    },
    // Maps icon type to the name of the icon
    componentIcon(type) {
      const iconMap = {
        "site_settings": "gear",
        "header": "edit-pencil",
        "hero_banner": "image-solid-full",
        "donatable_grid": "hand-heart",
        "article": "book-solid-full",
        "video": "video-solid-full",
        "footer": "list-solid-full",
      };
      return iconMap[type] || "document";
    },
    deleteComponentConfirmation(component) {
      this.deleteConfirmation = true;
      this.componentToDelete = component;
    },
    archive() {
      const component = this.componentToDelete;
      component.archived = true;
      this.landingStore.archiveComponentInDraft(component);
      this.components = this.components.filter(
        c => c.id !== component.id
      );
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'builder-delete-component' });
      this.componentToDelete = null;
      this.deleteConfirmation = false;
      this.handleSave();
    },
    getLandingPageLink() {
      const resolved = this.router.resolve({
        name: 'landing',
        params: { org: this.route.params.org }
      });
      return new URL(resolved.href, window.location.origin).toString();
    },
    getGivingFormLink() {
      const resolved = this.router.resolve({
        name: 'donation-form',
        params: { org: this.route.params.org }
      });
      return new URL(resolved.href, window.location.origin).toString();
    }
  },
};
</script>

<template>
  <div class="control-bar-container h-full">
    <div class="tab-container flex flex-row">
      <button
        :class="{'active': activeTab === 'edit'}"
        class="tab-header transition-colors duration-300"
        @click="switchTab('edit')"
      >
        Content
      </button>
      <button
        :class="{ 'active': activeTab === 'publish' }"
        class="tab-header transition-colors duration-300"
        @click="switchTab('publish')"
      >
        <icon
          v-if="landingStore.unpublishedChanges"
          icon="warning-outline"
          class="relative top-[2px]"
        />
        Publish
      </button>
    </div>
    <div class="editing-control-bar">
      <!-- CONTENT EDITING. All items controlled by builder Store. See Store for data structure -->
      <div
        v-show="activeTab === 'edit'"
        id="edit-tab"
        class="tab-content"
      >
        <div class="flex gap-3 justify-between mx-4">
          <!-- Desktop + label -->
          <div class="flex flex-col items-center">
            <div
              role="button"
              tabindex="0"
              class="cursor-pointer flex items-center justify-center w-10 h-10 rounded-md border transition-colors duration-200"
              :class="{
                'btn-filled': previewMode === 'desktop',
                'bg-transparent border-gray-300 hover:border-gray-300': previewMode !== 'desktop'
              }"
              @click="$emit('update:previewMode','desktop')"
            >
              <Icon
                icon="desktop"
                :color="previewMode === 'desktop' ? 'white' : 'gray'"
                size="20px"
              />
            </div>
            <span class="mt-1 text-[10px] text-gray-500">Desktop</span>
          </div>

          <!-- Tablet + label -->
          <div class="flex flex-col items-center">
            <div
              role="button"
              tabindex="0"
              class="cursor-pointer flex items-center justify-center w-10 h-10 rounded-md border transition-colors duration-200"
              :class="{
                'btn-filled': previewMode === 'tablet',
                'bg-transparent border-gray-300 hover:border-gray-300': previewMode !== 'tablet'
              }"
              @click="$emit('update:previewMode','tablet')"
            >
              <Icon
                icon="tablet"
                :color="previewMode === 'tablet' ? 'white' : 'gray'"
                size="20px"
              />
            </div>
            <span class="mt-1 text-[10px] text-gray-500">Tablet</span>
          </div>

          <!-- Mobile + label -->
          <div class="flex flex-col items-center">
            <div
              role="button"
              tabindex="0"
              class="cursor-pointer flex items-center justify-center w-10 h-10 rounded-md border transition-colors duration-200"
              :class="{
                'btn-filled': previewMode === 'mobile',
                'bg-transparent border-gray-300 hover:border-gray-300': previewMode !== 'mobile'
              }"
              @click="$emit('update:previewMode','mobile')"
            >
              <Icon
                icon="mobile"
                :color="previewMode === 'mobile' ? 'white' : 'gray'"
                size="20px"
              />
            </div>
            <span class="mt-1 text-[10px] text-gray-500">Mobile</span>
          </div>
        </div>

        <accordion-item
          v-for="(component, index) in pageData"
          v-if="!rearranging"
          :key="component.order"
          :ref="el => { if (el) accordionRefs[index] = el}"
          :class="['control-bar-item', component.siteSettings ? 'site-settings-item' : 'bg-gray-100']"
          @header-click="accordionClick(index, component.component_type)"
        >
          <!--  Global Settings -->
          <template #titleSlot>
            <div class="control-bar-item-header cursor-pointer">
              <div class="flex flex-row items-center gap-2">
                <icon
                  :icon="componentIcon(component.component_type)"
                  size="18px"
                  color="#555"
                />
                <div class="component-title">
                  {{ componentHeaderTitle(component.component_type, component) }}
                </div>

                <div
                  v-if="brandingChanged && component.siteSettings"
                  class="ml-2"
                >
                  <icon
                    icon="warning-outline"
                  />
                </div>
              </div>
            </div>
          </template>

          <template #bodySlot>
            <div
              v-if="component.siteSettings"
              class="control-bar-item-body"
            >
              <div class="rounded-lg">
                <div class="pb-4 pt-2 rounded-lg">
                  <Subtitle
                    text="Brand Colors"
                    tooltip-message="Set your site’s primary and accent colors."
                    :tooltip-position="{
                      top: '100%',
                      bottom: 'auto',
                      left: '50%',
                      transform: 'translateX(-50%)'
                    }"
                  />
                  <div class="flex gap-3 mt-4">
                    <div>
                      <CircleColorPicker
                        v-model="landingStore.localBrand.color_primary"
                        :disabled="landingStore.loading || landingStore.saving"
                        :color-label="'Prim'"
                      />
                    </div>
                    <div>
                      <CircleColorPicker
                        v-model="landingStore.localBrand.color_accent"
                        :disabled="landingStore.loading || landingStore.saving"
                        :color-label="'Accent'"
                      />
                    </div>
                  </div>
                  <div
                    v-if="!landingStore.localBrand.color_primary || !landingStore.localBrand.color_accent"
                    class="border border-yellow-300 bg-yellow-50 rounded-md p-4 flex items-start gap-2 text-sm text-yellow-800 mt-2"
                  >
                    <Icon
                      icon="warning-outline"
                      size="14px"
                      class="text-yellow-600 mt-0.5"
                    />
                    <div>
                      <p class="font-semibold">
                        Select Brand Color(s)
                      </p>
                      <p>Default colors will be applied if no primary or accent color is selected.</p>
                    </div>
                  </div>
                </div>

                <Subtitle
                  text="Organization Logo"
                  tooltip-message="Upload your main logo. Minimum size: 128px. JPG or PNG."
                  :tooltip-position="{
                    top: '100%',
                    bottom: 'auto',
                    left: '50%',
                    transform: 'translateX(-50%)'
                  }"
                />
                <button
                  v-if="!landingStore.localBrand.logo?.id || !landingStore.getResourceById(landingStore.localBrand.logo?.id)"
                  class="add-image-btn"
                  @click.stop="openImageColorPickerModal(landingStore.localBrand.logo)"
                >
                  <Icon
                    icon="plus"
                    color="gray"
                    size="16px"
                  />
                  Add Logo
                </button>
                <image-picker-card
                  v-else
                  :resource="landingStore.getResourceById(landingStore.localBrand.logo?.id)"
                  @click="openImageColorPickerModal(landingStore.localBrand.logo)"
                  @remove="confirmRemoveLogo"
                />

                <Subtitle
                  text="Organization Icon"
                  tooltip-message="Small icon shown in the browser tab. Uses the AllGive icon if left blank. "
                  class="mb-2 mt-4 text-sm"
                  :tooltip-position="iconTooltipPosition"
                />
                <button
                  v-if="!landingStore.localBrand.favicon?.id || !landingStore.getResourceById(landingStore.localBrand.favicon?.id)"
                  class="add-image-btn"
                  @click.stop="openImageColorPickerModal(landingStore.localBrand.favicon)"
                >
                  <Icon
                    icon="plus"
                    color="gray"
                    size="16px"
                  />
                  Add Icon
                </button>

                <image-picker-card
                  v-else
                  :resource="landingStore.getResourceById(landingStore.localBrand.favicon?.id)"
                  @click="openImageColorPickerModal(landingStore.localBrand.favicon)"
                  @remove="confirmRemoveIcon"
                />

                <button
                  :disabled="!saveGlobalsEnabled"
                  class="btn btn-filled btn-secondary flex justify-center mt-8"
                  @click="landingStore.saveGlobalSettings"
                >
                  <span v-if="landingStore.savingBrand">
                    <loading-spinner size="icon-small" />
                  </span>
                  <span v-else>
                    Save Brand Changes
                  </span>
                </button>
              </div>
            </div>

            <ul
              v-else
              class="control-bar-item-body"
            >
              <li
                v-for="(item, component_type) in component.component_data"
                :key="component_type"
              >
                <!--                 Only show this info box for the Website URL input -->
                <div
                  v-if="item.label === 'Website URL'"
                  class="border border-gray-300 bg-blue-50 rounded-md p-4 flex items-start gap-2 text-sm text-gray-800 mb-2"
                >
                  <Icon
                    icon="info-outline"
                    size="14px"
                    class="text-blue-600 mt-0.5"
                  />
                  <div>
                    <p class="font-semibold pb-2">
                      Website Link
                    </p>
                    <p>
                      If a URL is provided, clicking the logo on the landing page will redirect users to that link. If
                      left blank, the logo will not perform any navigation.
                    </p>
                  </div>
                </div>
                <label :for="component.component_type + '-' + component_type">
                  {{ item.label ? item.label : component_type }}
                </label>
                <span
                  v-if="item.label === 'Muted' && component?.component_data?.autoplay?.value"
                  class="text-xs text-gray-500 ml-1"
                >(required when autoplay is on)</span>
                <br>

                <!--               ARTICLE COMPONENT        -->
                <!--                 RICH TEXT AREA         -->
                <QuillEditor
                  v-if="item.type === 'rich-text'"
                  v-model:content="item.value"
                  :placeholder="item.placeholder || 'Enter text…'"

                  :toolbar="[
                    ['bold', 'italic', 'underline'],
                    [{ 'header': [1,2,3, false]} ],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                    ['link', 'blockquote'],
                    ['clean'],
                  ]"
                  theme="snow"
                  class="h-48 mb-2 rounded-md"
                  content-type="html"
                  @update:content="onFieldChange(item, component)"
                />
                <input
                  v-if="item.type === 'text'"
                  v-model="item.value"
                  class="text-black"
                  :placeholder="item.placeholder || 'Enter text…'"
                  @input="onFieldChange(item, component)"
                >

                <textarea
                  v-else-if="item.type === 'textarea'"
                  v-model="item.value"
                  :rows="item.rows || 3"
                  :placeholder="item.placeholder || ''"
                  @input="onFieldChange(item, component)"
                />
                <!-- Image Left or Right -->
                <select
                  v-if="item.type === 'select'"
                  v-model="item.value"
                  @change="onFieldChange(item, component)"
                >
                  <option
                    v-for="(option, key) in item.options"
                    :key="key"
                    :value="key"
                  >
                    {{ option }}
                  </option>
                </select>
                <image-picker-card
                  v-if="(item.type === 'image' || item.type === 'image-color')"
                  :resource="landingStore.getResourceById(item.resourceId)"
                  @click="openImageColorPickerModal(item)"
                  @remove="removeImage(item)"
                />
                <button
                  v-if="(item.type === 'image' || item.type === 'image-color') && (!item.resourceId || !landingStore.getResourceById(item.resourceId))"
                  class="btn btn-filled btn-secondary flex justify-center"
                  @click.stop="openImageColorPickerModal(item)"
                >
                  Select Image
                </button>

                <toggle-switch
                  v-if="item.type === 'switch'"
                  v-model="item.value"
                  :disabled="item.label === 'Muted' && component?.component_data?.autoplay?.value"
                  label="LABEL"
                  @change="onFieldChange(item, component)"
                />

                <button
                  v-if="item.type === 'campaign-sort-order'"
                  class="btn btn-filled btn-secondary flex justify-center"
                  @click="showCampaignSortModal = true"
                >
                  Sort Campaigns & Funds
                </button>
                <span
                  v-if="item.error"
                  class="text-xs text-red-600"
                >
                  {{ item.error }}
                </span>
              </li>
            </ul>
            <div class="flex mb-4">
              <p
                v-if="component.component_type === 'donatable_grid'"
                class="text-center px-4 small-text"
              >
                *This section controls only the layout of your funds and campaigns. To make changes, go to the
                <a
                  class="link-text"
                  @click="userStore.userScopedToSubdomain ?
                    router.push({name: 'client-dashboard-scoped', params: { panel: 'funds' } }) :
                    router.push({name: 'client-dashboard', params: { org: userStore.currentOrganization.slug, panel: 'funds' } })"
                >
                  Funds
                </a>
                and
                <a
                  class="link-text"
                  @click="userStore.userScopedToSubdomain ?
                    router.push({name: 'client-dashboard-scoped', params: { panel: 'campaigns' } }) :
                    router.push({name: 'client-dashboard', params: { org: userStore.currentOrganization.slug, panel: 'campaigns' } })"
                >
                  Campaigns
                </a>
                pages.
              </p>
            </div>
            <!--   DELETE COMPONENT BUTTON  -->
            <div
              v-if="!component.siteSettings && !['header', 'hero_banner', 'donatable_grid', 'footer'].includes(component.component_type)"
              class="flex justify-end px-4 pb-3"
            >
              <button
                class="btn bg-red-700 hover:bg-red-600  text-white flex items-center justify-center gap-2"
                @click.stop="deleteComponentConfirmation(component)"
              >
                <Icon
                  icon="trash"
                  class="icon-trash"
                  :size="'18px'"
                  :color="white"
                />
                Delete Section
              </button>
            </div>
          </template>
        </accordion-item>
      </div>

      <!-- Show save/discard buttons ONLY on Content tab -->
      <!-- Previously: class="sidebar-actions sticky bottom-0 mt-4 bg-white z-10" -->
      <div
        v-if="activeTab === 'edit'"
        class="sidebar-actions mt-4"
      >
        <!--  hiding buttons for prod push-->
        <div class="flex flex-row gap-2 ">
          <button
            v-if="!rearranging"
            class="btn btn-filled btn-secondary flex justify-center mb-4"
            @click="$emit('showAddComponentModal')"
          >
            Add Section
          </button>
        </div>
        <div class="flex flex-row gap-2 ">
          <button
            v-if="!rearranging"
            class="btn btn-filled btn-secondary flex justify-center mb-4"
            @click="rearranging = true"
          >
            Manage Layout
          </button>
        </div>
        <div
          v-if="!rearranging"
          class="flex flex-row gap-2"
        >
          <button
            :disabled="!canSaveOrDiscard"
            :class="[
              'btn btn-filled btn-secondary flex justify-center',
              canSaveOrDiscard ? 'btn-filled' : 'btn-disabled'
            ]"
            @click="handleSave()"
          >
            <span v-if="landingStore.saving"><loading-spinner size="icon-small" /> </span>
            <span v-else>Save Draft</span>
          </button>

          <!-- Discard Button -->
          <button
            :disabled="!canSaveOrDiscard"
            :class="[
              'btn btn-filled btn-secondary flex justify-center',
              canSaveOrDiscard ? 'btn-filled' : 'btn-disabled'
            ]"
            @click="handleDiscard()"
          >
            Discard
          </button>
        </div>
        <layout-manager
          v-if="rearranging"
          @close="rearranging = false"
        />
      </div>

      <!-- Publish Tab-->
      <div
        v-show="activeTab === 'publish'"
        id="publish-tab"
        class="tab-content"
      >
        <div class="flex flex-col gap-2 p-4">
          <p class="text-sm font-bold">
            Publish
          </p>
          <button
            :disabled="!canPublish"
            :class="[
              'font-semibold text-sm uppercase rounded-md py-2 flex items-center justify-center gap-2 w-full',
              canPublish ? 'button-filled' : 'btn-disabled'
            ]"
            @click="handlePublish"
          >
            <loading-spinner
              v-if="landingStore.publishing"
              size="icon-small"
            />
            <Icon
              v-else
              icon="upload_cloud"
              size="16px"
              color="white"
            />
            Publish To Live Site
          </button>
          <!-- Publish Section -->
          <div class="border border-gray-300 bg-blue-50 rounded-md p-4 flex items-start gap-2 text-sm text-gray-800">
            <Icon
              icon="info-outline"
              size="14px"
              class="text-blue-600 mt-0.5"
            />
            <div>
              <p class="font-semibold pb-2">
                Publishing
              </p>
              <p>
                Publishing immediately updates your live site with the current changes. These changes will be visible
                to your donors immediately.
              </p>
            </div>
          </div>
        </div>
        <p
          class="text-sm font-bold"
          style="padding: 0 16px"
        >
          Shareable QR
        </p>
        <div class="flex justify-center gap-8 p-4">
          <div class="share-qr">
            <p class="text-xs text-gray-500 mb-1">
              Landing Page
            </p>
            <QRComponent
              :value="getLandingPageLink()"
              :size="100"
            />
          </div>
          <div class="share-qr">
            <p class="text-xs text-gray-500 mb-1">
              Giving Form
            </p>
            <QRComponent
              :value="getGivingFormLink()"
              :size="100"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!--  Image / Color Picker for background images/ colors, article images, etc. Controlled by item type in store -->
  <base-modal
    v-if="showImageColorPicker"
    :title="imageColorPickerItem?.label || 'Select Image'"
    class="absolute"
    @close="closeImageColorPickerModal"
  >
    <tab-navigation
      v-if="imageColorPickerItem.type === 'image-color'"
      tab1-label="Image"
      tab2-label="Color"
    >
      <template #tab-1-content>
        <div class="image-picker-container">
          <image-picker
            v-model="selectedResourceId"
            @open-uploader="openUploadFromPicker"
          />
        </div>
      </template>
      <template #tab-2-content>
        <div>
          <color-list-picker
            v-model="imageColorPickerItem.colorValue"
            :color-options="colorOptions"
            @update:model-value="clearImage"
          />
        </div>
      </template>
    </tab-navigation>

    <image-picker
      v-else
      v-model="selectedResourceId"
      @open-uploader="openUploadFromPicker"
    />
  </base-modal>

  <!-- Multi Image uploader -->

  <base-modal
    v-if="showImageUploader"
    title="Add Image"
    @close="showImageUploader = false"
  >
    <div
      v-if="isUploadingCroppedImage"
      class="flex flex-col items-center justify-center py-12"
    >
      <loading-spinner size="small" />
      <p class="mt-4 text-sm text-gray-500">
        Uploading image…
      </p>
    </div>

    <div v-else-if="!croppingImageSrc">
      <drag-n-drop
        validate
        file-type="image"
        @file-selected="onImageSelected"
      />
    </div>

    <div v-else>
      <image-cropper
        :src="croppingImageSrc"
        :aspect-ratio="croppingTargetItem?.ratio"
        @update="onImageCropComplete"
        @cancel="resetImageCropper"
      />
    </div>
  </base-modal>

  <campaign-sorter
    v-if="showCampaignSortModal"
    :donation-targets="landingStore.donationTargets"
    :order="customSortOrder"
    @close="showCampaignSortModal = false"
    @save="saveCampaignSorting"
  />

  <!-- Popup Component -->
  <popup
    ref="popup"
    :message="popupMessage"
    :duration="1400"
  />

  <!--Remove Icon/Logo Confirmation-->
  <base-modal
    v-if="removeImageConfirmation"
    title="Remove Image?"
    size="small"
    @close="removeImageConfirmation = false"
  >
    <template #default>
      <p>Are you sure you want to remove this image?</p>
      <div class="mt-4 flex justify-end gap-2">
        <button
          class="btn btn-filled btn-secondary"
          @click="handleRemoveConfirm"
        >
          Remove
        </button>
        <button
          class="btn btn-outline btn-secondary"
          @click="removeImageConfirmation = false"
        >
          Cancel
        </button>
      </div>
    </template>
  </base-modal>

  <!--  Remove component Confirmation -->
  <base-modal
    v-if="deleteConfirmation"
    title="Remove Section?"
    size="small"
    @close="deleteConfirmation = false"
  >
    <template #default>
      <p>Are you sure you want to remove this section?</p>
      <div class="mt-4 flex justify-end gap-2">
        <button
          class="btn btn-filled btn-secondary"
          @click="archive"
        >
          Remove
        </button>
        <button
          class="btn btn-outline btn-secondary"
          @click="deleteConfirmation = false"
        >
          Cancel
        </button>
      </div>
    </template>
  </base-modal>
</template>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.control-bar-container {
  /* width: 20vw; — vw shifts when scrollbar appears; percentage is relative to parent which stays stable */
  width: 25%;
  @apply ml-4 mt-2;
  @apply border-2 border-r-0 rounded-l-md shadow-md;
  position: relative;
  height: 90dvh;
  @apply ml-4 h-full;
  border: 1px solid #ccc;
  border-right: none;
  border-radius: 0.375rem 0 0 0.375rem;
  @apply shadow-md;
  position: sticky;
  top: 10px;
  max-height: 90dvh;
  min-height: 90dvh;
  overflow-y: auto;
  z-index: 5;
  @apply h-full bg-gray-50;

  scrollbar-width: none;
  -ms-overflow-style: none; /* IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  .tab-container {
    height: 5%;
    position: sticky;
    top: 0;
    z-index: 10;
    @apply bg-white;

    .tab-header {
      @apply font-semibold text-base;
    }
  }

  .editing-control-bar {
    height: 100%;
  }

}

.editing-control-bar {
  @apply w-full p-4;
  color: #212121;
  background-color: #f9fafb;

  .control-bar-item {
    @apply my-3;
    border-color: #ccc !important;
    background-color: white;

    :deep(.accordion-header-container) {
      padding: 0.75rem 0.75rem;
    }

    .control-bar-item-header {
      font-weight: 600;
      color: $color-neutral-900;
      overflow: hidden;
      min-width: 0;
    }

    .control-bar-item-body {
      @apply px-4 pb-4 pt-2 m-0;
      @apply border-gray-200 border-t;
    }
  }

  label {
    @apply capitalize font-semibold text-sm;
  }

  input, textarea, select {
    color: #252525;
    width: 100%;
    padding: 10px;
    @apply border border-gray-400 rounded-md;
    margin-top: 5px;
    &:focus {
      border: 1px solid transparent;
      outline: 2px solid #007bff;
    }
  }

  .add-image-btn {
    @apply bg-white border-2 border-dashed border-gray-400 rounded-lg py-2 px-4 transition-colors flex items-center justify-center gap-2 w-full;
    @apply font-semibold;
    background-color: white !important;
    &:hover {
      border-color: #007bff;
      color: #007bff !important;
      :deep(svg) path {
        fill: #007bff;
      }
    }
  }
}

.editing-control-bar ul {
  list-style-type: none;
  @apply gap-3 flex flex-col;
}

.editing-control-bar button:not(.remove-image-btn) {
  width: 100%;
}

.remove-image-btn {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-disabled {
  background-color: #d1d5db !important;
  color: #6b7280 !important;
  cursor: not-allowed !important;
  pointer-events: none !important;
}

:deep(.ql-toolbar.ql-snow) {
  border-radius: 0.375rem 0.375rem 0 0;
}

:deep(.ql-container.ql-snow) {
  border-radius: 0 0 0.375rem 0.375rem;
}

.sidebar-actions :deep(.circle-progress-fill-line) {
  stroke: white !important;
}

.share-qr {
  @apply flex flex-col items-center justify-center;

  @media screen and (max-height: 768px) {
    :deep(button) {
      font-size: 0.7rem;
      padding: 0.25rem 0.5rem;
      margin-top: 0.25rem;
    }
  }
}

.component-title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}

.site-settings-item {
  border-color: $primary-color-light !important;
  box-shadow: none !important;
  outline: none !important;

  :deep(.accordion-item) {
    box-shadow: none !important;
    outline: none !important;
  }

  :deep(.accordion-header-container) {
    background-color: white !important;
  }
}

.link-text {
  font-weight: bold;
  color: #08602d;
}

.small-text {
  font-size: 12px;
}

/* Small viewport height adjustments (e.g. 1366x768) */
@media (max-height: 768px) {
  .control-bar-container {
    max-height: 85dvh;
    min-height: 85dvh;

    .tab-container {
      height: auto;
      min-height: 40px;
    }

    .editing-control-bar {
      height: calc(100% - 40px);
    }
  }
}
</style>
